/**
 * booking-step-1.js
 * -------------------
 * Page controller for Booking Step 1 ("Tell us about your event").
 *
 * Follows the shared lifecycle from booking-lifecycle.js:
 *   Load Booking State -> Validate/Render -> Handle interaction ->
 *   Save Booking State -> Navigate.
 *
 * Package resolution rule (fixes the old "Back keeps the previous
 * package" bug): if the URL has a `?package=` that matches a real entry
 * in this page's catalog, that selection ALWAYS wins and overwrites
 * whatever was previously stored — an explicit, fresh choice from the
 * user is never shadowed by stale state. Only when the URL has no
 * package at all do we fall back to the cached `booking.package` from a
 * previous visit.
 */
(function (window) {
  'use strict';

  var BookingStep1 = {
    init: function (root) {
      if (!root) return;

      var catalog = window.BookingCatalog.read(root);
      var normalizeKey = window.BookingCatalog.normalizeKey;
      var formatMoney = window.BookingCatalog.formatMoney;

      var emptyState = root.querySelector('[data-empty-state]');
      var dependentEls = root.querySelectorAll('[data-package-dependent]');

      // ---- Resolve which package (if any) applies to this page load ----
      function getPackageFromUrl() {
        var params;
        try {
          params = new URLSearchParams(window.location.search);
        } catch (e) {
          return null;
        }
        var key = normalizeKey(params.get('package'));
        return key && catalog[key] ? catalog[key] : null;
      }

      function resolvePackage(state) {
        var fromUrl = getPackageFromUrl();

        if (fromUrl) {
          // Explicit selection always wins and is persisted immediately,
          // so Booking State and the URL can never disagree.
          window.Booking.setPrimaryService({
            id: fromUrl.service ? normalizeKey(fromUrl.service) : fromUrl.key,
            package: fromUrl.key,
            service: fromUrl.service,
            name: fromUrl.name,
            packagePrice: fromUrl.packagePrice,
advancePrice: fromUrl.advancePrice,
            image: fromUrl.image,
            variantId: fromUrl.variantId
          });
          return fromUrl;
        }

        // No package in the URL — fall back to whatever was cached from
        // a previous step (e.g. user is simply re-entering this page).
        return state && state.package && state.package.key ? state.package : null;
      }

      // ---- Pill groups (event type / time) ----
      function setupPillGroup(groupName) {
        var group = root.querySelector('[data-pill-group="' + groupName + '"]');
        if (!group) return { getValue: function () { return ''; } };

        var selected = '';
        var buttons = group.querySelectorAll('[data-pill-value]');

        buttons.forEach(function (btn) {
          btn.addEventListener('click', function () {
            buttons.forEach(function (b) { b.classList.remove('is-selected'); });
            btn.classList.add('is-selected');
            selected = btn.dataset.pillValue;
            clearFieldError(groupName);
          });
        });

        return {
          getValue: function () { return selected; }
        };
      }

      // ---- Field error helpers ----
      function fieldWrapFor(name) {
        var errorEl = root.querySelector('[data-error="' + name + '"]');
        if (!errorEl) return null;
        return errorEl.closest('.bs1-simple-field, .bs1-field-group');
      }

      function setFieldError(name) {
        var wrap = fieldWrapFor(name);
        if (wrap) wrap.classList.add('bs1-field-invalid');
      }

      function clearFieldError(name) {
        var wrap = fieldWrapFor(name);
        if (wrap) wrap.classList.remove('bs1-field-invalid');
      }

      var eventTypeGroup = setupPillGroup('event-type');
      var eventTimeGroup = setupPillGroup('event-time');

      ['event-date', 'city', 'venue'].forEach(function (name) {
        var el = root.querySelector('[data-field="' + name + '"]');
        if (el) {
          el.addEventListener('input', function () { clearFieldError(name); });
          el.addEventListener('change', function () { clearFieldError(name); });
        }
      });

      var dateInput = root.querySelector('[data-field="event-date"]');
      var cityInput = root.querySelector('[data-field="city"]');
      var guestsInput = root.querySelector('[data-field="guests"]');
      var venueInput = root.querySelector('[data-field="venue"]');
      var landmarkInput = root.querySelector('[data-field="landmark"]');
      var notesInput = root.querySelector('[data-field="notes"]');

      function validate() {
        var firstInvalid = null;
        var isValid = true;

        function fail(name, el) {
          isValid = false;
          setFieldError(name);
          if (!firstInvalid) firstInvalid = el;
        }

        clearFieldError('event-type');
        clearFieldError('event-date');
        clearFieldError('event-time');
        clearFieldError('city');
        clearFieldError('venue');

        if (!eventTypeGroup.getValue()) fail('event-type', root.querySelector('[data-pill-group="event-type"]'));
        if (!dateInput.value) fail('event-date', dateInput);
        if (!eventTimeGroup.getValue()) fail('event-time', root.querySelector('[data-pill-group="event-time"]'));
        if (!cityInput.value) fail('city', cityInput);
        if (!venueInput.value.trim()) fail('venue', venueInput);

        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
      }

      var checkBtn = root.querySelector('[data-check-btn]');
      checkBtn.addEventListener('click', function () {
        if (!validate()) return;

        var eventData = {
          eventType: eventTypeGroup.getValue(),
          eventDate: dateInput.value,
          eventTime: eventTimeGroup.getValue(),
          city: cityInput.value,
          guests: guestsInput.value,
          venue: venueInput.value.trim(),
          landmark: landmarkInput.value.trim(),
          notes: notesInput.value.trim()
        };

        window.Booking.update('event', eventData);
        window.Booking.navigate(checkBtn.dataset.continueUrl);
      });

      // ---- Render: decide empty state vs. package card, then reveal ----
      function render(state) {
        var pkg = resolvePackage(state);

        if (!pkg) {
          emptyState.hidden = false;
          dependentEls.forEach(function (el) { el.hidden = true; });
          return;
        }

        emptyState.hidden = true;
        dependentEls.forEach(function (el) { el.hidden = false; });

        root.querySelectorAll('[data-pkg-service]').forEach(function (el) { el.textContent = pkg.service || ''; });
        root.querySelectorAll('[data-pkg-name]').forEach(function (el) { el.textContent = pkg.name || ''; });
        root.querySelectorAll('[data-pkg-package-price]').forEach(function(el){
    el.textContent = formatMoney(pkg.packagePrice);
});

root.querySelectorAll('[data-pkg-advance-price]').forEach(function(el){
    el.textContent = formatMoney(pkg.advancePrice);
});

root.querySelectorAll('[data-pkg-balance-price]').forEach(function(el){
    el.textContent = formatMoney(
        pkg.packagePrice - pkg.advancePrice
    );
});

root.querySelectorAll('[data-pkg-advance]')
    .forEach(function(el){
        el.textContent = formatMoney(pkg.advancePrice);
    });

root.querySelectorAll('[data-pkg-balance]')
    .forEach(function(el){
        el.textContent = formatMoney(
            pkg.packagePrice - pkg.advancePrice
        );
    });
        root.querySelectorAll('[data-pkg-price-footer]').forEach(function (el) {
    el.textContent = formatMoney(pkg.advancePrice);
});
        root.querySelectorAll('[data-pkg-image]').forEach(function (el) {
          if (pkg.image) {
            el.src = pkg.image;
            el.alt = pkg.name || '';
          } else {
            var wrap = el.closest('.bs1-package-image');
            if (wrap) wrap.style.display = 'none';
          }
        });
      }

      window.BookingLifecycle.run({ root: root, render: render });
    }
  };

  window.BookingStep1 = BookingStep1;
})(window);